import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PolicyPageLayout, {
  PolicySection, Para, BulletList, BulletItem, SubHead, InfoBox, ContactCard,
} from '../components/policy/PolicyPageLayout';

const toc = [
  { id: 'what-are-cookies', label: 'What Are Cookies?' },
  { id: 'how-we-use',       label: 'How We Use Cookies' },
  { id: 'cookies-we-use',   label: 'Cookies We Use' },
  { id: 'local-storage',    label: 'Local Storage' },
  { id: 'third-party',      label: 'Third-Party Cookies' },
  { id: 'managing',         label: 'Managing Cookies' },
  { id: 'changes',          label: 'Changes to This Policy' },
  { id: 'contact',          label: 'Contact' },
];

const related = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms of Service', href: '/terms-of-service' },
  { label: 'Data Processing Agreement', href: '/data-processing-agreement' },
];

interface CookieRowProps {
  name: string;
  type: string;
  duration: string;
  purpose: string;
}

const CookieRow: React.FC<CookieRowProps> = ({ name, type, duration, purpose }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr 1.2fr 2.5fr', borderBottom: '1px solid #e5e7eb', '&:hover': { background: '#f9fafb' } }}>
    {[name, type, duration, purpose].map((cell, i) => (
      <Box key={i} sx={{ px: 2, py: 1.5, fontSize: '0.875rem', color: '#374151', fontFamily: i === 0 ? 'monospace' : 'inherit', wordBreak: 'break-word' }}>
        {cell}
      </Box>
    ))}
  </Box>
);

const CookiePolicyPage: React.FC = () => (
  <PolicyPageLayout title="Cookie Policy" lastUpdated="April 26, 2026" toc={toc} related={related}>

    <PolicySection id="what-are-cookies" number="1" title="What Are Cookies?">
      <Para>
        Cookies are small text files that a website places on your device when you visit. They are
        widely used to make websites work efficiently, remember your preferences, and provide information
        to website operators. Cookies cannot run programs or deliver viruses to your device.
      </Para>
      <Para>
        In addition to cookies, we also use browser local storage and session storage — technologies
        that serve a similar purpose but store data directly on your device without expiry through the
        network.
      </Para>
    </PolicySection>

    <PolicySection id="how-we-use" number="2" title="How We Use Cookies">
      <Para>CruvzMail uses cookies and similar technologies for the following purposes:</Para>
      <BulletList>
        <BulletItem>
          <strong>Authentication:</strong> Keeping you logged in between page loads and tab sessions
        </BulletItem>
        <BulletItem>
          <strong>Security:</strong> CSRF protection tokens to prevent cross-site request forgery attacks
        </BulletItem>
        <BulletItem>
          <strong>Preferences:</strong> Remembering your theme (light/dark), language, layout, and
          display density settings
        </BulletItem>
        <BulletItem>
          <strong>Session management:</strong> Tracking active sessions and multi-account state
        </BulletItem>
        <BulletItem>
          <strong>Performance:</strong> Understanding how users navigate the webmail to improve speed
          and usability
        </BulletItem>
      </BulletList>
      <InfoBox>
        CruvzMail does not use advertising cookies, tracking cookies, or any cookies that share your
        data with third-party advertisers.
      </InfoBox>
    </PolicySection>

    <PolicySection id="cookies-we-use" number="3" title="Cookies We Use">
      <Para>The following cookies are set by CruvzMail:</Para>
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', mt: 2, mb: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr 1.2fr 2.5fr', background: '#1f2937' }}>
          {['Cookie Name', 'Type', 'Duration', 'Purpose'].map((h) => (
            <Box key={h} sx={{ px: 2, py: 1.25, color: '#f9fafb', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {h}
            </Box>
          ))}
        </Box>
        {/* Rows */}
        <CookieRow name="cruvz_access" type="Essential" duration="15 minutes" purpose="JWT access token used to authenticate API requests. HttpOnly, Secure, SameSite=Strict." />
        <CookieRow name="cruvz_refresh" type="Essential" duration="7 days" purpose="JWT refresh token used to silently renew the access token. HttpOnly, Secure, SameSite=Strict." />
        <CookieRow name="cruvz_csrf" type="Security" duration="Session" purpose="CSRF protection token validated on every state-changing request." />
        <CookieRow name="cruvz_theme" type="Preference" duration="1 year" purpose="Stores your selected UI theme (light, dark, or system)." />
        <CookieRow name="cruvz_lang" type="Preference" duration="1 year" purpose="Stores your preferred language/locale setting." />
        <CookieRow name="cruvz_session" type="Essential" duration="Session" purpose="Tracks active browser session. Deleted when you close the browser." />
      </Box>
      <Para>
        We use HttpOnly and Secure flags on authentication cookies, meaning they cannot be accessed by
        JavaScript and are only transmitted over HTTPS connections.
      </Para>
    </PolicySection>

    <PolicySection id="local-storage" number="4" title="Local Storage and Session Storage">
      <Para>
        In addition to cookies, we use your browser's local storage and session storage to improve
        performance:
      </Para>
      <BulletList>
        <BulletItem>
          <strong>Local storage:</strong> Cached folder counts, unread counts, contact autocomplete,
          draft content, and UI state (sidebar collapse, column widths). Persists across browser sessions.
        </BulletItem>
        <BulletItem>
          <strong>Session storage:</strong> Temporary compose window state, search history for the
          current tab, and navigation breadcrumbs. Cleared when the browser tab is closed.
        </BulletItem>
      </BulletList>
      <Para>
        None of the data stored in local or session storage is transmitted to third parties.
      </Para>
    </PolicySection>

    <PolicySection id="third-party" number="5" title="Third-Party Cookies">
      <Para>
        CruvzMail does not embed third-party advertising networks, social media widgets, or analytics
        trackers that set their own cookies. The only potential third-party cookie is:
      </Para>
      <BulletList>
        <BulletItem>
          <strong>Razorpay:</strong> When you visit the billing page or initiate a payment, Razorpay
          may set cookies on their payment domain for fraud prevention and PCI compliance purposes.
          These are subject to Razorpay's own privacy policy.
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="managing" number="6" title="Managing and Disabling Cookies">
      <SubHead>Browser Controls</SubHead>
      <Para>
        You can control cookies through your browser settings. Most browsers allow you to view, block,
        or delete cookies stored by websites:
      </Para>
      <BulletList>
        <BulletItem>
          <strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data
        </BulletItem>
        <BulletItem>
          <strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data
        </BulletItem>
        <BulletItem>
          <strong>Safari:</strong> Preferences → Privacy → Manage Website Data
        </BulletItem>
        <BulletItem>
          <strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data
        </BulletItem>
      </BulletList>
      <SubHead>Impact of Disabling Cookies</SubHead>
      <Para>
        Essential cookies (authentication and CSRF) are required for CruvzMail to function. Disabling
        them will prevent you from logging in. Preference cookies are optional — disabling them means
        your theme and language settings will reset on each visit.
      </Para>
      <SubHead>Clearing Local Storage</SubHead>
      <Para>
        You can clear local storage from your browser's developer tools (Application → Local Storage →
        Clear). This will reset UI preferences and cached data but will not affect your email data
        stored on our servers.
      </Para>
    </PolicySection>

    <PolicySection id="changes" number="7" title="Changes to This Policy">
      <Para>
        We may update this Cookie Policy when we add or remove technologies. We will notify registered
        users of significant changes via email. The "Last Updated" date at the top of this page
        indicates when this policy was last revised.
      </Para>
    </PolicySection>

    <PolicySection id="contact" number="8" title="Contact Us">
      <Para>If you have questions about our use of cookies:</Para>
      <ContactCard>
        <strong>Privacy:</strong> privacy@cruvzmail.com<br />
        <strong>General support:</strong> support@cruvzmail.com<br />
        <br />
        <strong>CruvzMail</strong> — operated by Cruvz Online Shopping India Pvt Ltd<br />
        cruvzmail.com
      </ContactCard>
    </PolicySection>

  </PolicyPageLayout>
);

export default CookiePolicyPage;
